// Report de Momento X — fluxo CX (report_version = 2) + trava de reports pendentes.
//
// Fonte única de textos, chaves e regras do formulário novo. Três origens de texto:
//  - Figma ("TELA TRAVA ORÁCULO MX", frames 36:6019 / 569:3461 / 569:3694 / 52:2914): VERBATIM, não reescrever;
//  - [app]: textos que o Oráculo já usa hoje (alerta "Acesso bloqueado", botões);
//  - NOVO: textos criados na página "Evolução UI/UX (jornada)" do canvas — ficam SÓ aqui, agrupados em
//    `NEW_COPY`, para o CX revisar num lugar só.
//
// As CHAVES dos motivos são as de `backend-core/app/Constants/AppointmentReportReasons.php` (é o que se grava
// e o que o backend valida com `Rule::in`). Os rótulos podem mudar sem reinterpretar o histórico; as chaves, nunca.
import {
  FINAL_APPROVAL,
  GROUP_DINAMIC,
  INDIVIDUAL_INTERVIEW,
  INTERVIEW,
  NEGOTIATION,
  PARTNERSHIP,
  PRE_JOB_SAMPLE,
  X_MOMENT,
} from '@/constants/appointment_types'

// Versão do formulário gravada em `report_version` e enviada no rascunho (`form_version`).
// Igual a `AppointmentReportReasons::VERSION_CX`.
export const FORM_VERSION = 2

// ---------------------------------------------------------------------------------------------------------------
// Respostas (mesmos nomes do payload de `POST appointments/{id}/report/finalize` e de `answers` no rascunho)
// ---------------------------------------------------------------------------------------------------------------
export const HAD_MEETING = 'had_meeting'
export const CLOSED_DEAL = 'closed_deal'
export const OFFER_MADE = 'offer_made'
export const HAD_SPECIALIST_SUPPORT = 'had_specialist_support'
export const NOT_HELD_REASON = 'not_held_reason'
export const OFFER_NOT_MADE_REASON = 'offer_not_made_reason'

export const YES_LABEL = 'Sim'
export const NO_LABEL = 'Não'

// Sim sempre à esquerda, Não à direita (Figma). Nenhum valor padrão: as duas começam vazias.
export const YES_NO_OPTIONS = [
  { value: true, label: YES_LABEL },
  { value: false, label: NO_LABEL },
]

// Perguntas Sim | Não na ordem do frame 569:3694 (Realizada → Negócio fechado → Oferta → Especialista).
export const QUESTIONS = [
  { key: HAD_MEETING, label: 'A Reunião foi realizada?' },
  { key: CLOSED_DEAL, label: 'Negócio foi fechado?' },
  { key: OFFER_MADE, label: 'Houve oferta de produtos do ecossistema?' },
  { key: HAD_SPECIALIST_SUPPORT, label: 'Houve um especialista suporte lhe auxiliando?' },
]

// ---------------------------------------------------------------------------------------------------------------
// Motivos (selects). value = chave do backend; label = texto do Figma, na ordem do Figma.
// ---------------------------------------------------------------------------------------------------------------
export const REASON_PLACEHOLDER = 'Selecione um motivo'

export const NOT_HELD_REASONS = [
  { value: 'client_no_show', label: 'Cliente não compareceu na hora da reunião' },
  { value: 'pn_personal', label: 'Não pude comparecer por motivos pessoais' },
  { value: 'pn_forgot_confirm', label: 'Esqueci de confirmar a reunião com o cliente com antecedência' },
  { value: 'client_no_return', label: 'Cliente não retornou o contato para fechar o agendamento' },
  { value: 'client_asked_reschedule', label: 'Cliente pediu para remarcar a data do compromisso' },
]

export const OFFER_NOT_MADE_REASONS = [
  { value: 'offer_not_presented', label: 'Não apresentei a oferta na reunião' },
  { value: 'offer_deprioritized', label: 'Priorizei outros assuntos da reunião e não cheguei a esse ponto' },
  { value: 'offer_price_high', label: 'Cliente achou o valor/custo muito alto' },
  { value: 'offer_has_similar', label: 'Cliente já possui um produto/serviço similar' },
  { value: 'offer_compliance', label: 'Restrição regulatória ou de compliance que impediu a oferta' },
  { value: 'offer_wrong_fit', label: 'Ofereci a solução errada para o perfil daquele cliente' },
]

// Cada select aparece sob a pergunta que o libera (`parent`) quando ela vale `when`.
export const REASON_SELECTS = {
  [NOT_HELD_REASON]: {
    key: NOT_HELD_REASON,
    label: 'Por que a reunião não foi realizada?',
    options: NOT_HELD_REASONS,
    parent: HAD_MEETING,
    when: false,
  },
  [OFFER_NOT_MADE_REASON]: {
    key: OFFER_NOT_MADE_REASON,
    label: 'Por que a oferta não foi realizada?',
    options: OFFER_NOT_MADE_REASONS,
    parent: OFFER_MADE,
    when: false,
  },
}

const labelOf = (options, value) => {
  const option = options.find(item => item.value === value)
  return option ? option.label : undefined
}

export const notHeldReasonLabel = value => labelOf(NOT_HELD_REASONS, value)
export const offerNotMadeReasonLabel = value => labelOf(OFFER_NOT_MADE_REASONS, value)

export const questionLabel = key => {
  const question = QUESTIONS.find(item => item.key === key)
  if (question) return question.label
  return REASON_SELECTS[key] ? REASON_SELECTS[key].label : undefined
}

// ---------------------------------------------------------------------------------------------------------------
// Regras (a MESMA matriz que `FinalizeMomentXReportRequest` valida no backend)
//  - "A Reunião foi realizada?" é obrigatória;
//  - Não → só o motivo de não realização (obrigatório); as outras perguntas não se aplicam;
//  - Sim (ou ainda sem resposta) → Negócio fechado, Oferta e Especialista obrigatórias;
//  - Oferta = Não → motivo da oferta obrigatório.
// Respostas de um caminho escondido ficam guardadas no rascunho (voltam se a resposta mudar de novo), mas nunca
// contam para a validação nem vão no payload.
// ---------------------------------------------------------------------------------------------------------------
export const isYesNoAnswered = value => value === true || value === false

const isValidReason = (key, value) => !!REASON_SELECTS[key]
  && REASON_SELECTS[key].options.some(option => option.value === value)

/**
 * Itens do caminho ativo, na ordem da tela: `{ key, kind: 'question' | 'reason', answered }`.
 * Antes de responder a primeira pergunta, o caminho considerado é o do "Sim" (mostra as 4 perguntas).
 */
export const reportItems = answers => {
  const values = answers || {}
  const question = key => ({ key, kind: 'question', answered: isYesNoAnswered(values[key]) })
  const reason = key => ({ key, kind: 'reason', answered: isValidReason(key, values[key]) })

  const items = [question(HAD_MEETING)]
  if (values[HAD_MEETING] === false) {
    items.push(reason(NOT_HELD_REASON))
    return items
  }
  items.push(question(CLOSED_DEAL))
  items.push(question(OFFER_MADE))
  if (values[OFFER_MADE] === false) items.push(reason(OFFER_NOT_MADE_REASON))
  items.push(question(HAD_SPECIALIST_SUPPORT))

  return items
}

export const missingReportItems = answers => reportItems(answers).filter(item => !item.answered)

export const isReportComplete = answers => missingReportItems(answers).length === 0

/** Só os campos do caminho ativo — é o corpo de `FINALIZE_MX_REPORT`. */
export const buildFinalizePayload = answers => {
  const values = answers || {}
  if (values[HAD_MEETING] === false) {
    return {
      [HAD_MEETING]: false,
      [NOT_HELD_REASON]: values[NOT_HELD_REASON],
    }
  }
  const payload = {
    [HAD_MEETING]: true,
    [CLOSED_DEAL]: values[CLOSED_DEAL],
    [OFFER_MADE]: values[OFFER_MADE],
    [HAD_SPECIALIST_SUPPORT]: values[HAD_SPECIALIST_SUPPORT],
  }
  if (values[OFFER_MADE] === false) payload[OFFER_NOT_MADE_REASON] = values[OFFER_NOT_MADE_REASON]

  return payload
}

/** Linhas do "Resumo do report" (tela MX Salvo): `{ key, label, value }` do caminho respondido. */
export const reportSummaryLines = answers => {
  const values = answers || {}
  const yesNo = value => {
    if (value === true) return YES_LABEL
    if (value === false) return NO_LABEL
    return undefined
  }

  return reportItems(values)
    .filter(item => item.answered)
    .map(item => ({
      key: item.key,
      label: questionLabel(item.key),
      value: item.kind === 'question'
        ? yesNo(values[item.key])
        : labelOf(REASON_SELECTS[item.key].options, values[item.key]),
    }))
}

// ---------------------------------------------------------------------------------------------------------------
// Figma — cabeçalho, stepper, botões e avisos
// ---------------------------------------------------------------------------------------------------------------
export const APPOINTMENT_FIELD_LABELS = {
  customer: 'Cliente',
  consultant: 'PN Responsável',
  specialist: 'PN Especialista',
  eventType: 'Tipo de Compromisso',
  start: 'Data de Início',
  end: 'Data de Término',
}

export const STEPPER_STEPS = ['Novo compromisso', 'Report de compromisso']

export const FIGMA_COPY = {
  pageTitle: 'Report de compromisso',
  newAppointmentTitle: 'Novo compromisso',
  finalizeButton: 'Finalizar Momento X',
  savedTitle: 'MX Salvo',
  dateLock: 'O formulário será liberado no horário marcado da sua reunião!',
  back: 'Voltar',
  save: 'Salvar',
  selectFormat: 'Selecione um formato',
  meetingLinkPlaceholder: 'https://link.com.br',
  specialistPlaceholder: 'Selecione um PN Especialista',
  datePlaceholder: '__/__/__ | --:--',
}

// ---------------------------------------------------------------------------------------------------------------
// [app] — textos que o Oráculo já usa hoje
// ---------------------------------------------------------------------------------------------------------------
export const APP_COPY = {
  momentX: 'Momento X',
  appointments: 'Compromissos',
  lockTitle: 'Acesso bloqueado',
  lockText: 'Seu acesso ficará bloqueado enquanto não finalizar o report dos compromissos. '
    + 'Para reabilitar o sistema, finalize o report.',
  continue: 'Continuar',
  helpPrefix: 'Dúvidas? Fale conosco:',
  onlineSupport: 'Atendimento online',
  editAppointment: 'Editar Compromisso',
  reportDetails: 'Detalhes do report',
  appointmentCreated: 'Compromisso criado com sucesso!',
  alreadyReported: 'Report já realizado anteriormente!',
  // Falha ao carregar o compromisso (mesmo texto da antiga AppointmentReportSidebar).
  loadReportError: 'Ocorreu um erro ao carregar o compromisso para report. Entre em contato com o setor de TI.',
  // Pós-report com negócio fechado → contract-save (a pergunta da antiga AppointmentReportSidebar; hoje a Parceria
  // fechada dos tipos padrão).
  contractReportQuestion: 'Deseja realizar o report dos contratos agora ou reportar depois?',
  reportNow: 'Reportar agora',
  reportLater: 'Reportar depois',
  notOwner: 'Somente o PN Responsável pode fazer o report deste compromisso.',
  // Mesmo texto da recusa 422 NOT_REPORTABLE de `AppointmentReportFlowController`: o Report o mostra quando o servidor
  // recusa o rascunho/finalize e também ao abrir um compromisso que saiu do funil sem report.
  notReportable: 'Este compromisso não está mais agendado (foi cancelado, excluído ou remarcado) e não pode '
    + 'receber report.',
  meetingLinkLabel: 'Link da Reunião',
  meetingFormatLabel: 'Formato de Reunião',
  meetingAddressLabel: 'Endereço da reunião',
  invalidMeetingUrl: 'Você deve informar uma URL válida para a reunião online',
  customerHasMomentX: 'Este cliente já possui um compromisso do tipo Momento X agendado.',
  sameSpecialist: 'O PN especialista e o PN responsável não podem ser a mesma pessoa.',
  timeAlreadyBooked: 'Horário já reservado para outro compromisso',
  // Novo compromisso — textos e regras da NewAppointmentSidebar e mensagens 405 de `POST /api/appointments`
  // (`AppointmentController::create`), exibidas na página cheia junto do campo a que se referem.
  customerEmailLabel: 'E-mail do cliente',
  leadMomentXUnavailable: 'Compromissos do tipo Momento X não estão disponíveis para leads',
  epRunningPrefix: 'Este cliente já possui um contrato de Entrevista Paga (EP) com status',
  epRunningStatus: 'Rodando',
  otherOwnerPrefix: 'Você está registrando uma atividade para um cliente pertencente a',
  otherOwnerSuffix: '. Para fins de carteira, indicadores e produção, este registro será atribuído ao dono do '
    + 'cliente. Você continuará identificado como responsável pela execução.',
  customerOfOtherPn: 'Não é possivel agendar compromissos para outro PN responsável.',
  candidateStageBusy: 'Este candidato ja possui compromisso ativo para esta etapa.',
}

// [app] Nomes dos tipos de compromisso como o Oráculo mostra hoje (`appify_appointments_envent_type.description`).
// A lista de pendências da API traz só o id do tipo (`event_type`).
export const APPOINTMENT_TYPE_LABELS = {
  [INTERVIEW]: 'Entrevista',
  [X_MOMENT]: 'Momento X',
  [PARTNERSHIP]: 'Parceria',
  [NEGOTIATION]: 'Negociação de EP',
  [INDIVIDUAL_INTERVIEW]: 'Entrevista individual',
  [GROUP_DINAMIC]: 'Dinâmica em grupo',
  [PRE_JOB_SAMPLE]: 'Pré Job Sample',
  [FINAL_APPROVAL]: 'Aprovação Final',
}

export const appointmentTypeLabel = eventType => APPOINTMENT_TYPE_LABELS[Number(eventType)] || ''

// ---------------------------------------------------------------------------------------------------------------
// NOVO — textos criados na página "Evolução UI/UX (jornada)" (validar com o CX)
// ---------------------------------------------------------------------------------------------------------------
const plural = (count, one, many) => (Number(count) === 1 ? one : many)

export const NEW_COPY = {
  // Report — formulário
  meetingLink: 'Abrir link da reunião',
  online: 'Online',
  inPerson: 'Presencial',
  answerRequired: 'Responda esta pergunta para finalizar.',
  missingAnswers: count => plural(count, 'Falta 1 resposta', `Faltam ${count} respostas`),
  readyToFinalize: 'Tudo pronto para finalizar',
  draftSavedAt: time => `Rascunho salvo às ${time}`,
  finalizing: 'Finalizando…',
  retry: 'Tentar novamente',
  finalizeError: 'Não foi possível finalizar o report. Suas respostas foram mantidas. Tente novamente.',
  shortcutHint: 'Ctrl ↵',
  stepDone: '(concluída)',
  stepLocked: '(bloqueada)',

  // Report — aguardando horário
  releasesIn: (remaining, time) => `Libera em ${remaining} · às ${time}`,
  joinMeeting: 'Entrar na reunião',
  formReleased: 'Formulário liberado',
  // Tipos padrão antes do horário: o "não houve reunião" já pode ser finalizado (cancelamento antecipado). O status
  // da barra com "Sim" usa `reportAvailableAtMeeting` (do Novo compromisso). Validar com o CX.
  earlyNotHeldHint: 'Se a reunião não vai acontecer, responda "Não" e finalize o report agora.',

  // Report — MX Salvo
  finalizedBy: (name, date, time) => `Finalizado por ${name} em ${date} às ${time}`,
  summaryTitle: 'Resumo do report',
  backToAppointments: 'Voltar para compromissos',
  remainingPendencies: count => plural(
    count,
    'Falta 1 pendência para liberar seu acesso.',
    `Faltam ${count} pendências para liberar seu acesso.`,
  ),
  nextPendency: 'Próxima pendência',
  allPendencies: 'Ver todas as pendências',
  // MX + negócio fechado: "Cadastrar contrato" (→ contract-save) só sem pendências restantes; com pendências,
  // avisa que o cadastro fica para depois. Os dois textos não estão no canvas — validar com o CX.
  registerContract: 'Cadastrar contrato',
  contractAfterPendencies: 'Você poderá cadastrar o contrato assim que finalizar os reports pendentes.',

  // Trava / central de pendências
  pendencyPosition: (position, total) => `Pendência ${position} de ${total}`,
  lockProgressCaption: 'Seu acesso é liberado quando todas forem reportadas.',
  menuLockedCaption: 'Disponível após finalizar os reports pendentes',
  pendenciesTitle: 'Pendências',
  resolvedCount: (resolved, total) => `${resolved} de ${total}`,
  resolvedSuffix: 'resolvidas',
  daysOverdue: days => plural(days, '1 dia de atraso', `${days} dias de atraso`),
  draftStarted: 'Rascunho iniciado',
  resolved: 'Resolvida',
  reportSaved: 'Report salvo',
  reportSavedFor: name => `Report salvo: ${name}`,
  finalizeLegacyButton: 'Finalizar report',
  unlockedTitle: 'Acesso liberado',
  unlockedText: 'Tudo certo. Você já pode usar o Oráculo normalmente.',
  // Falha ao carregar a lista da central (não está no canvas — validar com o CX); o botão é `retry`.
  pendenciesLoadError: 'Não foi possível carregar as pendências.',

  // Novo compromisso
  reportAvailableAtMeeting: 'Disponível no horário da reunião',
  reportAvailableAt: (time, date) => `Disponível às ${time} de ${date}`,
  recentCustomers: 'Recentes',
  noResults: 'Nenhum resultado encontrado',
  fieldRequired: 'Preencha este campo para salvar.',
  selfSuffix: name => `${name} (você)`,
  onlyManagersCanChange: 'Somente gestores podem alterar.',
  invalidDate: 'Data ou horário inválido.',
  // Início que já passou (Novo compromisso, reagendamento da sidebar e "Deseja remarcar a reunião?" do report). Mesmo
  // texto do 405 de `AppointmentController::PAST_START_MESSAGE` — o servidor recusa do mesmo jeito.
  startInPast: 'Não é possível agendar um compromisso para um horário que já passou.',
  customerPlaceholder: 'Nome do seu cliente',
  descriptionLabel: 'Descrição (opcional)',
  descriptionPlaceholder: 'Qual o objetivo dessa reunião?',
  appointmentSummaryTitle: 'Resumo do compromisso',
  reportReleaseGeneric: 'O report é liberado no horário de início da reunião.',
  reportReleaseAt: (time, date) => `O report será liberado às ${time} de ${date}. `
    + 'Finalize até 23:59 do mesmo dia para evitar o bloqueio do acesso.',
  reportReleaseToast: (time, date) => `O report será liberado às ${time} de ${date}.`,
  conflictsWith: (label, range) => `Conflita com: ${label}, ${range}`,
  durationLabel: 'Duração',
  duration: value => `Duração: ${value}`,
  quickToday: 'Hoje',
  quickTomorrow: 'Amanhã',
  quickNextWeek: '+1 semana',
  missingFields: count => plural(count, 'Falta 1 campo', `Faltam ${count} campos`),
  reviewFields: count => plural(count, 'Revise 1 campo', `Revise ${count} campos`),
  readyToSave: 'Tudo pronto para salvar',
  saving: 'Salvando…',
  goToReport: 'Ir para o report',
  // "Negociação com Judith Mossman" — 1ª parte de `conflictsWith` (board v4-novo-compromisso).
  conflictAppointmentLabel: (type, name) => `${type} com ${name}`,
}

export const APPOINTMENT_DURATIONS = [
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 h' },
  { minutes: 90, label: '1 h 30' },
  { minutes: 120, label: '2 h' },
]

// ---------------------------------------------------------------------------------------------------------------
// Códigos de erro da API (campo `code` do corpo) — ver `backend-core/app/Exceptions/*` e o controller do fluxo
// ---------------------------------------------------------------------------------------------------------------
export const REPORT_ERROR_CODES = {
  PENDING: 'APPOINTMENT_REPORT_PENDING', // 423 — trava (o interceptor redireciona para a central)
  NOT_OPEN: 'APPOINTMENT_REPORT_NOT_OPEN', // 423 — antes do horário (opens_at, server_now)
  ALREADY_REPORTED: 'APPOINTMENT_ALREADY_REPORTED', // 409 — já existe report (report_id)
  DRAFT_CONFLICT: 'APPOINTMENT_REPORT_DRAFT_CONFLICT', // 409 — rascunho mudou em outra aba (draft atual no corpo)
  NOT_OWNER: 'APPOINTMENT_REPORT_NOT_OWNER', // 422 — só o PN Responsável reporta
  // O servidor não manda mais este código (todo tipo finaliza na mesma rota): fica como marca local dos formulários
  // para "este compromisso é do outro formulário" (ex.: Momento X aberto no StandardReportForm).
  WRONG_TYPE: 'APPOINTMENT_REPORT_WRONG_TYPE',
  NOT_REPORTABLE: 'APPOINTMENT_NOT_REPORTABLE', // 422 — compromisso cancelado/excluído
}

// ---------------------------------------------------------------------------------------------------------------
// NOVO — rótulos só para leitor de tela (aria-label dos boards da página "Evolução UI/UX (jornada)")
// ---------------------------------------------------------------------------------------------------------------
export const A11Y_COPY = {
  steps: 'Etapas',
  appointmentData: 'Dados do compromisso',
  pendenciesContext: 'Pendências de report',
  pendenciesProgress: 'Pendências resolvidas',
}

// Rotas do fluxo (nomes do vue-router)
export const REPORT_ROUTE = 'appointment-report'
export const REPORT_HUB_ROUTE = 'report-pendencias'
export const NEW_APPOINTMENT_ROUTE = 'appointment-new'
// Tela de Compromissos do menu (`router/routes/customer.js`; mesmo path `/compromissos` de `appointment-list`).
export const APPOINTMENTS_ROUTE = 'appointments'

// `?origem=pendencias` no report abre o contexto da central ("Pendência N de M", "← Pendências").
export const REPORT_ORIGIN_QUERY = 'origem'
export const REPORT_ORIGIN_PENDENCIES = 'pendencias'

// `?voltar=/caminho` na central: a tela que o PN pediu antes de ser levado para lá (gravada pelo guard de rotas e
// pelo 423 do interceptor). "Continuar", com o acesso liberado, volta para ela (sem ela, vai para o dashboard).
export const REPORT_HUB_RETURN_QUERY = 'voltar'

/** Só caminhos internos do Oráculo ("/clientes?x=1"); nunca URL externa ("//site", "https://…"). */
export const isSafeReturnPath = value => typeof value === 'string'
  && value.charAt(0) === '/'
  && value.charAt(1) !== '/'
  && value.charAt(1) !== '\\'

const isMeaningfulParam = value => value !== undefined && value !== null && value !== false && value !== ''

/**
 * A rota leva estado em params que NÃO estão no path (ex.: `contract-save` aberto com `{ appointment }` para cadastrar
 * o contrato do compromisso): esse estado não sobrevive ao `?voltar=`, e o "Continuar" abriria outra tela (um
 * contrato em branco, sem o compromisso). Params do path (`:id`) vão no `fullPath` e não contam.
 */
const hasStateOutsidePath = route => {
  const matched = route && Array.isArray(route.matched) ? route.matched : []
  const record = matched[matched.length - 1]
  const keys = record && record.regex && Array.isArray(record.regex.keys) ? record.regex.keys : null
  if (!keys) return false
  const pathParams = keys.map(key => String(key.name))
  const params = route.params || {}

  return Object.keys(params).some(name => !pathParams.includes(name) && isMeaningfulParam(params[name]))
}

/** A tela pode ir no `?voltar=` da central: não é a própria central nem depende de params fora do path. */
export const isReportHubReturnTarget = route => !!route
  && route.name !== REPORT_HUB_ROUTE
  && !hasStateOutsidePath(route)

/**
 * Destino da central guardando a tela pedida (`route` = rota do vue-router). Sem uma tela que possa voltar
 * (`isReportHubReturnTarget`), vai sem `?voltar=` — o "Continuar" leva ao dashboard.
 */
export const reportHubLocation = route => {
  const fullPath = isReportHubReturnTarget(route) ? route.fullPath : ''
  if (!isSafeReturnPath(fullPath) || fullPath === '/') return { name: REPORT_HUB_ROUTE }

  return { name: REPORT_HUB_ROUTE, query: { [REPORT_HUB_RETURN_QUERY]: fullPath } }
}
